import { useEffect, useRef, useState } from 'preact/hooks';
import type { ResolveAddressRequest, ResolveAddressResponse } from '@ks-studio/shared';
import type { DeclineReason } from '@ks-studio/shared';
import type { WidgetState } from '../state';
import type { Suggestion } from '../lib/places';
import { track } from '../lib/analytics';

/**
 * Step 1. Pick an address, put it through the service-area gate.
 *
 * `suggest` and `resolve` are INJECTED rather than imported, matching how the
 * callables take a `deps` object: it is what lets every branch below be tested
 * without a network, a key or a loaded Maps script.
 */
export type Step1Props = {
  state: WidgetState;
  update: (patch: Partial<WidgetState>) => void;
  suggest: (input: string) => Promise<Suggestion[]>;
  resolve: (req: ResolveAddressRequest) => Promise<ResolveAddressResponse>;
  /** Keystroke debounce. Every fired request is billable, so this is a cost control. */
  debounceMs?: number;
};

/**
 * What the customer is told for each way the gate can say no.
 *
 * THREE REASONS, THREE MESSAGES, pinned by a test. Collapsing them into one
 * "sorry" is the easy regression and the customer notices: being in the wrong
 * state, being in a part of New York we have not reached, and us failing to
 * place the address are different facts with different follow-ups. The last is
 * not a "no" at all, which is why it asks them to try again.
 *
 * COPY IS UNREVIEWED. It states no price, no saving and no incentive, so it
 * clears ADR-0016 on its face, but nobody has signed it off and this repo has no
 * automated copy gate. `location_unknown` in particular is reachable in mock
 * mode only through a trigger in `lib/api.ts`, which exists because
 * dac-qualifier shipped an unreachable screen carrying unreviewed copy.
 */
const DECLINE_COPY: Record<DeclineReason, string> = {
  out_of_state:
    'Kasselman Solar installs in New York State only. It looks like this address is somewhere else.',
  out_of_area:
    'We install across upstate New York, but we have not reached this address yet. Long Island and New York City are outside the area we cover.',
  location_unknown:
    'We could not place that address precisely enough to look at the roof. Try selecting it again from the list, or give us a call and we will check it by hand.',
};

export function Step1Address({
  state,
  update,
  suggest,
  resolve,
  debounceMs = 250,
}: Step1Props) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  /** Index of the arrow-key highlighted option, or -1 for none. */
  const [active, setActive] = useState(-1);
  const [declined, setDeclined] = useState<DeclineReason | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Held local rather than in `WidgetState`, along with the four above. The rule
   * this repo now follows: `WidgetState` carries what crosses a step boundary,
   * and anything a step needs only while it is mounted stays here. A declined
   * address cannot reach step 2 by construction, so no later step can read this.
   */
  const latestQuery = useRef('');

  /**
   * Set when `choose` writes the picked address back into the input, so the
   * lookup effect skips the change it caused itself.
   *
   * Without this, selecting a suggestion reopens the dropdown underneath the
   * result: `choose` empties the list and then sets `query` to the chosen text,
   * `query` is the effect's dependency, and the effect dutifully looks it up
   * again. Every selection therefore spent a second billable autocomplete
   * request and left the list sitting open over the answer. Caught by driving
   * the real page, not by the suite, which only ever asserted what came after
   * the click.
   */
  const skipNextLookup = useRef(false);

  // Debounced autocomplete. The timer is cleared on every keystroke and on
  // unmount, so a request never lands against a component that is gone, and a
  // stale response never overwrites a newer one (guarded by latestQuery).
  useEffect(() => {
    if (skipNextLookup.current) {
      skipNextLookup.current = false;
      return;
    }
    if (!query.trim()) {
      setSuggestions([]);
      setActive(-1);
      return;
    }
    latestQuery.current = query;
    const timer = setTimeout(() => {
      void suggest(query)
        .then((next) => {
          if (latestQuery.current !== query) return;
          setSuggestions(next);
          setActive(-1);
        })
        .catch(() => {
          // A failed lookup is not worth a message of its own: the customer can
          // keep typing, and the empty list already says "nothing matched".
          if (latestQuery.current === query) setSuggestions([]);
        });
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, suggest, debounceMs]);

  async function choose(s: Suggestion) {
    setSuggestions([]);
    setActive(-1);
    skipNextLookup.current = true;
    setQuery(s.text);
    setDeclined(null);
    setError(null);
    setBusy(true);
    if (state.notice !== null) update({ notice: null });
    try {
      /**
       * A FRESH TOKEN PER ATTEMPT. Google: "Once a session has concluded, the
       * token is no longer valid; your app must generate a fresh token for each
       * new session." Minting it here rather than once per mount is what makes
       * that true for a customer who picks, gets declined, and picks again.
       *
       * Maps JS never sees this value; see the note in lib/places.ts for why the
       * two halves cannot be paired.
       */
      const sessionToken = crypto.randomUUID();
      const res = await resolve({
        placeId: s.placeId,
        sessionToken,
        submissionId: state.submissionId,
      });

      if (!res.served) {
        setDeclined(res.reason);
        track('address_declined', { step: 'address', reason: res.reason });
        return;
      }

      update({
        resolved: {
          formatted: res.formatted,
          county: res.county,
          lat: res.lat,
          lng: res.lng,
        },
        addressToken: res.addressToken,
        step: 'contact',
      });
      track('address_resolved', { step: 'address', county: res.county });
    } catch {
      // Retryable, and said so. The address has not been surrendered anywhere:
      // nothing is signed and no lead exists until step 2.
      setError('Something went wrong checking that address. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const listboxId = 'address-suggestions';
  const optionId = (i: number) => `${listboxId}-${i}`;

  /**
   * Keyboard handling for the listbox, which is not optional decoration: an
   * autocomplete whose only affordance is a mouse click is unusable by keyboard
   * and by a screen reader, and it is the input that keeps focus throughout.
   * That is why the options are plain `<li>` elements carrying `role="option"`
   * rather than buttons: a focusable control nested inside an option is invalid
   * ARIA, and it would also steal the focus this pattern depends on staying put.
   */
  function onKeyDown(e: KeyboardEvent) {
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      // Only swallow Enter when it means "take the highlighted option". Left to
      // bubble otherwise, so Enter still submits once this sits inside a form.
      // `noUncheckedIndexedAccess` is on, so the index is optional and the
      // guard is not ceremony: `active` is held across renders while
      // `suggestions` is replaced by every debounced response, so a reply that
      // lands between the arrow key and Enter genuinely can leave it out of
      // range.
      const chosen = active >= 0 ? suggestions[active] : undefined;
      if (chosen) {
        e.preventDefault();
        void choose(chosen);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setActive(-1);
    }
  }

  return (
    <section>
      <h2>Where is the roof?</h2>

      <div class="field">
        <label>
          Street address
          <input
            type="text"
            role="combobox"
            aria-expanded={suggestions.length > 0}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={active >= 0 ? optionId(active) : undefined}
            autocomplete="street-address"
            value={query}
            disabled={busy}
            onInput={(e) => setQuery(e.currentTarget.value)}
            onKeyDown={onKeyDown}
          />
        </label>
      </div>

      {suggestions.length > 0 && (
        <ul id={listboxId} role="listbox" class="suggestions">
          {suggestions.map((s, i) => (
            <li
              key={s.placeId}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              class={i === active ? 'active' : undefined}
              onClick={() => void choose(s)}
            >
              {s.text}
            </li>
          ))}
        </ul>
      )}

      {/* Sent here by step 2 when the address token died. Rendered before the
          decline, because arriving with no explanation is the failure this exists to
          prevent. Cleared as soon as they pick something, in `choose`. */}
      {state.notice !== null && (
        <p class="notice" role="alert">
          {state.notice}
        </p>
      )}

      {declined !== null && (
        <p class="notice" role="alert">
          {DECLINE_COPY[declined]}
        </p>
      )}

      {error !== null && (
        <p class="notice" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
